import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import NotificationsContainer from 'shared/notifications-container';
import ErrorsCatcher from 'shared/errors-catcher';
import { Provider as RootPropsProvider } from './root-props';

import { underscoreToCamelNested } from './transformations';

export function createMounter(store) {
  return (selector, Component, props, options = { router: false }) => {
    try {
      const node = typeof selector === 'string' ? document.querySelector(selector) : selector;
      const WrapperComponent = options.router ? Router : Fragment;

      if (!(node instanceof Node)) {
        return;
      }

      const routerProps = {
        ...(options.router && {
          history: createBrowserHistory(),
          path: props.path,
        }),
      };

      const camelCasedProps = underscoreToCamelNested(props || {});

      ReactDOM.render(
        <Provider store={store}>
          <RootPropsProvider value={camelCasedProps}>
            <WrapperComponent {...routerProps}>
              <Fragment>
                <NotificationsContainer />
                <ErrorsCatcher>
                  <Component {...camelCasedProps} />
                </ErrorsCatcher>
              </Fragment>
            </WrapperComponent>
          </RootPropsProvider>
        </Provider>,
        node
      );
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('mountReactComponent ERROR:', error);
      }

      throw error;
    }
  };
}

export default function mountComponents(components, componentsPropsGetter, store) {
  const mounter = createMounter(store);
  const componentNodes = document.querySelectorAll('[data-react-component]');
  // NodeList at non-latest browsers doesn't support forEach
  for (let i = 0; i < componentNodes.length; i += 1) {
    const componentNode = componentNodes[i];
    const data = componentNode.dataset;
    const componentName = data.reactComponent;
    const Component = components[componentName];

    if (!Component) {
      throw new Error(`You are trying initialize unregistered react component "${componentName}"!`);
    }

    const props = componentsPropsGetter()[componentName];

    mounter(componentNode, Component, props, {
      router: Object.prototype.hasOwnProperty.call(data, 'withRouter'),
    });
  }
}
